import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import { API_BASE } from '@/lib/api';
import {
  Upload, Download, CheckCircle2, AlertCircle, FileText, Loader2,
  ArrowRight, ArrowLeft, ChevronRight, Settings2, AlertTriangle, X
} from 'lucide-react';
import { toast } from 'sonner';

const steps = ['Select Type', 'Upload CSV', 'Map Columns', 'Validate', 'Import'];

export default function BulkImportPage() {
  const [step, setStep] = useState(0);
  const [importType, setImportType] = useState('vendors');
  const [jobId, setJobId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('vs_token');
    window.open(`${API_BASE}/bulk-import/template/${importType}?token=${token}`, '_blank');
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_type', importType);
      const { data } = await api.post('/bulk-import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setJobId(data.job_id);
      setUploadResult(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
      toast.success(`CSV parsed: ${data.total_rows} rows detected`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setLoading(false); }
  };

  const handleValidate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/bulk-import/${jobId}/validate`, { column_mapping: mapping });
      setValidation(data);
      setStep(3);
      if (data.error_count > 0) {
        toast.error(`${data.error_count} rows have errors`);
      } else {
        toast.success(`All ${data.valid_count} rows are valid!`);
      }
    } catch (err) {
      toast.error('Validation failed');
    } finally { setLoading(false); }
  };

  const handleExecute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/bulk-import/${jobId}/execute`);
      setImportResult(data);
      setStep(4);
      toast.success(`Import complete: ${data.success_count} records created`);
    } catch (err) {
      toast.error('Import failed');
    } finally { setLoading(false); }
  };

  const handleDownloadErrors = () => {
    const token = localStorage.getItem('vs_token');
    window.open(`${API_BASE}/bulk-import/${jobId}/errors?token=${token}`, '_blank');
  };

  const reset = () => {
    setStep(0);
    setJobId(null);
    setUploadResult(null);
    setMapping({});
    setValidation(null);
    setImportResult(null);
  };

  const schema = uploadResult?.schema || {};
  const allFields = [...(schema.required || []), ...(schema.optional || [])];

  return (
    <div data-testid="bulk-import-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Bulk Import</h1>
        <p className="text-sm text-muted-foreground">Import vendors, properties, or assignments from CSV</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors duration-200 ${
              step > i ? 'bg-[hsl(142,56%,36%)] text-white' : step === i ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>{step > i ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
            <span className={`text-sm hidden sm:inline ${step === i ? 'font-medium' : 'text-muted-foreground'}`}>{s}</span>
            {i < steps.length - 1 && <Separator className="w-4 sm:w-8" />}
          </div>
        ))}
      </div>

      {/* Step 0: Select Type */}
      {step === 0 && (
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Select Import Type</CardTitle>
            <CardDescription>Choose what you'd like to import</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={importType} onValueChange={setImportType}>
              <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vendors">Vendors</SelectItem>
                <SelectItem value="properties">Properties</SelectItem>
                <SelectItem value="assignments">Vendor-Property Assignments</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-3">
              <Button data-testid="bulk-import-download-template-button" variant="outline" onClick={handleDownloadTemplate}>
                <Download className="mr-2 h-4 w-4" /> Download Template
              </Button>
              <Button onClick={() => setStep(1)}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Upload CSV */}
      {step === 1 && (
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload CSV File</CardTitle>
            <CardDescription>Upload a CSV file with your {importType} data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div data-testid="bulk-import-dropzone"
              className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/50 transition-colors duration-200 cursor-pointer"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
              onDragLeave={e => e.currentTarget.classList.remove('border-primary')}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); handleUpload(e.dataTransfer.files[0]); }}
            >
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => handleUpload(e.target.files[0])} />
              {loading ? (
                <Loader2 className="mx-auto h-10 w-10 text-primary animate-spin mb-2" />
              ) : (
                <Upload className="mx-auto h-10 w-10 text-muted-foreground/40 mb-2" />
              )}
              <p className="text-sm text-muted-foreground">{loading ? 'Parsing CSV...' : 'Click or drag & drop your CSV file'}</p>
              <p className="text-xs text-muted-foreground mt-1">CSV files only</p>
            </div>
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && uploadResult && (
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <Settings2 className="inline mr-2 h-4 w-4" /> Map Columns
            </CardTitle>
            <CardDescription>{uploadResult.total_rows} rows • {uploadResult.headers.length} columns detected</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allFields.map(field => (
                <div key={field} className="flex items-center gap-3">
                  <div className="w-40">
                    <Label className="text-sm capitalize">{field.replace('_', ' ')}</Label>
                    {schema.required?.includes(field) && <span className="text-destructive ml-1">*</span>}
                  </div>
                  <Select value={mapping[field] || ''} onValueChange={v => setMapping(m => ({ ...m, [field]: v }))}>
                    <SelectTrigger className="flex-1" data-testid={`mapping-${field}`}><SelectValue placeholder="Select column" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">— Skip —</SelectItem>
                      {uploadResult.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Sample Preview */}
            {uploadResult.sample_rows?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Preview (first {Math.min(uploadResult.sample_rows.length, 3)} rows):</p>
                <ScrollArea className="max-h-48">
                  <Table>
                    <TableHeader>
                      <TableRow>{uploadResult.headers.map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadResult.sample_rows.slice(0, 3).map((row, i) => (
                        <TableRow key={i}>
                          {uploadResult.headers.map(h => <TableCell key={h} className="text-xs py-1">{row[h] || ''}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
              <Button data-testid="bulk-import-validate-button" onClick={handleValidate} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Validate <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Validate */}
      {step === 3 && validation && (
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Validation Results</CardTitle>
            <CardDescription>{validation.total_rows} rows checked</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card className="rounded-lg border-[hsl(142_40%_78%)] bg-[hsl(142_56%_97%)]">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="mx-auto h-6 w-6 text-[hsl(142,56%,36%)] mb-1" />
                  <p className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{validation.valid_count}</p>
                  <p className="text-xs text-muted-foreground">Valid rows</p>
                </CardContent>
              </Card>
              <Card className={`rounded-lg ${validation.error_count > 0 ? 'border-[hsl(0_70%_82%)] bg-[hsl(0_85%_97%)]' : 'border-border'}`}>
                <CardContent className="p-4 text-center">
                  <AlertCircle className={`mx-auto h-6 w-6 mb-1 ${validation.error_count > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                  <p className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{validation.error_count}</p>
                  <p className="text-xs text-muted-foreground">Errors</p>
                </CardContent>
              </Card>
              <Card className="rounded-lg">
                <CardContent className="p-4 text-center">
                  <FileText className="mx-auto h-6 w-6 text-muted-foreground mb-1" />
                  <p className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{validation.total_rows}</p>
                  <p className="text-xs text-muted-foreground">Total rows</p>
                </CardContent>
              </Card>
            </div>

            {/* Error details */}
            {validation.error_rows?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-destructive flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Errors Found</p>
                  {jobId && <Button variant="outline" size="sm" onClick={handleDownloadErrors}><Download className="mr-1 h-3 w-3" /> Download Error Report</Button>}
                </div>
                <ScrollArea className="max-h-64">
                  <div className="space-y-2">
                    {validation.error_rows.slice(0, 20).map((err, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[hsl(0_85%_97%)] border border-[hsl(0_70%_90%)]">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="destructive" className="text-xs">Row {err.row_number}</Badge>
                        </div>
                        {err.errors?.map((e, j) => (
                          <p key={j} className="text-xs text-destructive ml-4">• {e.field}: {e.error}</p>
                        ))}
                      </div>
                    ))}
                    {validation.error_rows.length > 20 && <p className="text-xs text-muted-foreground text-center">... and {validation.error_rows.length - 20} more errors</p>}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Valid rows preview */}
            {validation.preview_rows?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Preview of valid rows:</p>
                <ScrollArea className="max-h-48">
                  <Table>
                    <TableHeader>
                      <TableRow>{Object.keys(validation.preview_rows[0].data || {}).filter(k => k !== '_row_number').map(k => <TableHead key={k} className="text-xs capitalize">{k.replace('_', ' ')}</TableHead>)}</TableRow>
                    </TableHeader>
                    <TableBody>
                      {validation.preview_rows.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {Object.entries(row.data || {}).filter(([k]) => k !== '_row_number').map(([k, v]) => (
                            <TableCell key={k} className="text-xs py-1">{String(v)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="mr-2 h-4 w-4" /> Fix Mapping</Button>
              <Button data-testid="bulk-import-confirm-button" onClick={handleExecute} disabled={loading || validation.valid_count === 0}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Import {validation.valid_count} Valid Rows
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Complete */}
      {step === 4 && importResult && (
        <Card className="rounded-xl shadow-[var(--shadow-soft)]">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(142,56%,36%)] mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Import Complete</h3>
            <p className="text-sm text-muted-foreground mb-4">{importResult.success_count} records imported successfully</p>
            {importResult.error_count > 0 && (
              <div className="mb-4">
                <Badge variant="destructive" className="mb-2">{importResult.error_count} errors</Badge>
                <Button variant="outline" size="sm" className="ml-2" onClick={handleDownloadErrors}><Download className="mr-1 h-3 w-3" /> Error Report</Button>
              </div>
            )}
            <Button onClick={reset}>New Import</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
